import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Trash2, Edit2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { departmentsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Department Manager Component
 * Allows DPOs to view, create, and manage departments for an entity
 */
export function DepartmentManager({ entityId, entityName }) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingStatus, setEditingStatus] = useState('active');

  useEffect(() => {
    fetchDepartments();
  }, [entityId]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentsAPI.getByEntity(entityId);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDept = async () => {
    if (!newDeptName.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      const response = await departmentsAPI.create({
        dpo_org_id: user?.dpo_org_id,
        entity_id: entityId,
        name: newDeptName.trim(),
        status: 'active'
      });

      setDepartments([...departments, response.data]);
      setNewDeptName('');
      setIsAdding(false);
      toast.success('Department created');
    } catch (error) {
      console.error('Failed to create department:', error);
      toast.error(error.response?.data?.detail || 'Failed to create department');
    }
  };

  const handleUpdateDept = async (deptId) => {
    if (!editingName.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      const response = await departmentsAPI.update(deptId, {
        name: editingName.trim(),
        status: editingStatus
      });

      setDepartments(departments.map(d => d.id === deptId ? response.data : d));
      setEditingId(null);
      toast.success('Department updated');
    } catch (error) {
      console.error('Failed to update department:', error);
      toast.error(error.response?.data?.detail || 'Failed to update department');
    }
  };

  const handleDeleteDept = async (deptId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }

    try {
      await departmentsAPI.delete(deptId);
      setDepartments(departments.filter(d => d.id !== deptId));
      toast.success('Department deleted');
    } catch (error) {
      console.error('Failed to delete department:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const startEdit = (dept) => {
    setEditingId(dept.id);
    setEditingName(dept.name);
    setEditingStatus(dept.status);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingStatus('active');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Departments</CardTitle>
        <CardDescription>Manage departments for {entityName}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add New Department */}
        {!isAdding && (
          <Button
            onClick={() => setIsAdding(true)}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Department
          </Button>
        )}

        {isAdding && (
          <div className="flex gap-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <Input
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              placeholder="Department name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddDept();
                if (e.key === 'Escape') {
                  setIsAdding(false);
                  setNewDeptName('');
                }
              }}
              autoFocus
            />
            <Button
              size="sm"
              onClick={handleAddDept}
              className="whitespace-nowrap"
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewDeptName('');
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Departments Table */}
        {loading ? (
          <div className="text-center py-4 text-muted-foreground">
            Loading departments...
          </div>
        ) : departments.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No departments yet
          </div>
        ) : (
          <div className="space-y-2">
            {departments.map(dept => (
              <div
                key={dept.id}
                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg"
              >
                {editingId === dept.id ? (
                  <div className="flex gap-2 flex-1">
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateDept(dept.id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                    <Select value={editingStatus} onValueChange={setEditingStatus}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={() => handleUpdateDept(dept.id)}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <div className="font-medium">{dept.name}</div>
                      <div className="text-xs text-muted-foreground">
                        Created {new Date(dept.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge
                      variant={dept.status === 'active' ? 'default' : 'secondary'}
                      className="ml-2"
                    >
                      {dept.status}
                    </Badge>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(dept)}
                        className="p-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteDept(dept.id)}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
